import logger from '@/layers/support/monitoring/logger';
import { useEffect, useRef } from 'react';

const LOG_MODULE = 'FE [Presentation / Components / JotformEmbed]';

interface JotformEmbedProps {
  jotformId: string;
  onSubmit: () => void;
  prefillData?: Record<string, any>;
  width?: string;
  height?: string;
}

export default function JotformEmbed({
  jotformId,
  onSubmit,
  prefillData,
  width = '100%',
  height = '500px',
}: JotformEmbedProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  logger.debug(`${LOG_MODULE}`, { jotformId, onSubmit, prefillData, iframeRef });

  const buildJotformUrl = () => {
    const baseUrl = `https://form.jotform.com/${jotformId}`;

    if (!prefillData) {
      logger.debug(`${LOG_MODULE} buildJotformUrl: no prefill data`, { baseUrl });
      return baseUrl;
    }

    const queryParams = new URLSearchParams();
    Object.entries(prefillData).forEach(([key, option]) => {
      queryParams.append(key, option);
    });

    const result = `${baseUrl}?${queryParams.toString()}`;

    logger.debug(`${LOG_MODULE} buildJotformUrl: with prefill data`, {
      baseUrl,
      queryParams,
      result,
    });
    return result;
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      logger.debug(`${LOG_MODULE} handleMessage`, { event });

      if (event.origin.includes('jotform.com')) {
        if (event.data.action === 'submission-completed' && event.data.formID === jotformId) {
          onSubmit();
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [jotformId, onSubmit]);

  return (
    <iframe ref={iframeRef} src={buildJotformUrl()} style={{ width, height, border: 'none' }} />
  );
}
